import types from '../mutation-types';
import HelpCenterSearchAPI from '../../api/helpCenterSearch';
import { throwErrorMessage } from '../utils/api';

export const state = {
  config: null,
  status: null,
  portals: [],
  kbStats: null,
  uiFlags: {
    isFetchingConfig: false,
    isSavingConfig: false,
    isDeletingConfig: false,
    isTestingConfig: false,
    isReindexing: false,
    isFetchingStatus: false,
    isFetchingPortals: false,
    isUpdatingPortal: false,
    isFetchingKbStats: false,
    isImportingFile: false,
  },
};

export const getters = {
  getConfig($state) {
    return $state.config;
  },
  getStatus($state) {
    return $state.status;
  },
  getPortals($state) {
    return $state.portals;
  },
  getKbStats($state) {
    return $state.kbStats;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  fetchConfig: async ({ commit }) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isFetchingConfig: true });
    try {
      const { data } = await HelpCenterSearchAPI.getConfig();
      commit(types.SET_HC_SEARCH_CONFIG, data && data.id ? data : null);
    } catch (error) {
      // ignore — empty config returns {} which has no id
      commit(types.SET_HC_SEARCH_CONFIG, null);
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isFetchingConfig: false });
    }
  },

  saveConfig: async ({ commit }, configData) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isSavingConfig: true });
    try {
      const { data } = await HelpCenterSearchAPI.saveConfig(configData);
      commit(types.SET_HC_SEARCH_CONFIG, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isSavingConfig: false });
    }
    return null;
  },

  deleteConfig: async ({ commit }) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isDeletingConfig: true });
    try {
      await HelpCenterSearchAPI.deleteConfig();
      commit(types.SET_HC_SEARCH_CONFIG, null);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isDeletingConfig: false });
    }
  },

  testConfig: async ({ commit }, configData) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isTestingConfig: true });
    try {
      const { data } = await HelpCenterSearchAPI.testConfig(configData);
      return data;
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Test failed';
      throw new Error(message);
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isTestingConfig: false });
    }
  },

  reindex: async ({ commit }) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isReindexing: true });
    try {
      const { data } = await HelpCenterSearchAPI.reindex();
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isReindexing: false });
    }
    return null;
  },

  fetchStatus: async ({ commit }) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isFetchingStatus: true });
    try {
      const { data } = await HelpCenterSearchAPI.getStatus();
      commit(types.SET_HC_SEARCH_STATUS, data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isFetchingStatus: false });
    }
  },

  fetchKbStats: async ({ commit }) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isFetchingKbStats: true });
    try {
      const { data } = await HelpCenterSearchAPI.getKnowledgeStats();
      commit(types.SET_HC_SEARCH_KB_STATS, data);
    } catch (error) {
      // ignore — panel simply shows empty state
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isFetchingKbStats: false });
    }
  },

  importFile: async ({ commit }, payload) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isImportingFile: true });
    try {
      const { data } = await HelpCenterSearchAPI.importFile(payload);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isImportingFile: false });
    }
    return null;
  },

  fetchPortals: async ({ commit }) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isFetchingPortals: true });
    try {
      const { data } = await HelpCenterSearchAPI.getPortals();
      commit(types.SET_HC_SEARCH_PORTALS, data || []);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isFetchingPortals: false });
    }
  },

  updatePortal: async ({ commit }, { id, internal }) => {
    commit(types.SET_HC_SEARCH_UI_FLAG, { isUpdatingPortal: true });
    try {
      const { data } = await HelpCenterSearchAPI.updatePortal(id, { internal });
      commit(types.UPDATE_HC_SEARCH_PORTAL, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_HC_SEARCH_UI_FLAG, { isUpdatingPortal: false });
    }
    return null;
  },
};

export const mutations = {
  [types.SET_HC_SEARCH_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_HC_SEARCH_CONFIG]($state, config) {
    $state.config = config;
  },
  [types.SET_HC_SEARCH_STATUS]($state, status) {
    $state.status = status;
  },
  [types.SET_HC_SEARCH_KB_STATS]($state, kbStats) {
    $state.kbStats = kbStats;
  },
  [types.SET_HC_SEARCH_PORTALS]($state, portals) {
    $state.portals = portals;
  },
  [types.UPDATE_HC_SEARCH_PORTAL]($state, updated) {
    $state.portals = $state.portals.map(p =>
      p.id === updated.id ? updated : p
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
